import { useState, useEffect } from "react";

export default function MontgomeryHeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Floating Nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="/" className="flex items-center gap-2">
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${scrolled ? "bg-[#1A365D]" : "bg-white/20"} transition-colors`}>
              <i className={`ri-passport-line text-xl ${scrolled ? "text-white" : "text-white"}`}></i>
            </div>
            <span className={`font-bold text-lg transition-colors ${scrolled ? "text-[#1A365D]" : "text-white"}`}>
              Fast Passport Center
            </span>
          </a>
          <div className="hidden md:flex items-center gap-6">
            {["Services", "How It Works", "Pricing", "About"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-medium transition-colors cursor-pointer ${
                  scrolled ? "text-gray-600 hover:text-[#1A365D]" : "text-white/80 hover:text-white"
                }`}
              >
                {item}
              </a>
            ))}
            <a
              href="https://fastpassportcenter.com/checkout/"
              className={`text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-[#1A365D] text-white hover:bg-[#13294B]"
                  : "bg-[#F6C344] text-[#1A365D] hover:bg-[#e5b430]"
              }`}
            >
              Apply Now
            </a>
          </div>
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center ${scrolled ? "text-[#1A365D]" : "text-white"}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"}></i>
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-4 py-4 flex flex-col gap-3">
            {["Services", "How It Works", "Pricing", "About"].map((item) => (
              <a key={item} href={`#${item.toLowerCase().replace(/\s/g, "-")}`} className="text-gray-600 text-sm font-medium py-2">
                {item}
              </a>
            ))}
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-[#1A365D] text-white text-sm font-bold px-5 py-2.5 rounded-md text-center whitespace-nowrap"
            >
              Apply Now
            </a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20downtown%20Montgomery%20Alabama%20skyline%20at%20golden%20hour%2C%20the%20historic%20Alabama%20State%20Capitol%20dome%20with%20its%20classical%20white%20columns%2C%20the%20Alabama%20River%20winding%20through%20the%20city%2C%20modern%20government%20buildings%20alongside%20historic%20antebellum%20architecture%2C%20warm%20sunset%20light%20reflecting%20on%20the%20river%2C%20magnolia%20trees%20and%20lush%20greenery%2C%20professional%20drone%20photography%20with%20rich%20warm%20tones%20and%20dramatic%20sky%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=montgomery-hero-img&orientation=landscape"
          alt="Montgomery Alabama downtown skyline with Alabama State Capitol at sunset"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-24 pb-16 text-center">
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Emergency &amp; Expedited Passport Services in Montgomery, AL
          </h1>
          <p className="text-white/80 text-lg md:text-xl max-w-3xl mx-auto mb-8 leading-relaxed">
            Maxwell Air Force Base issuing PCS orders to Japan with only 14 days notice? A state legislator invited to an international trade mission in South Korea? An Alabama State University student accepted to a study abroad program in Ghana? We help Montgomery and the River Region — from Prattville to Wetumpka to Millbrook — get passports fast. As a registered U.S. Department of State courier, we offer rates typically 30–100% lower than FedEx, Staples, and other resellers. As fast as 24 hours. A+ BBB rated. No office visit required.
          </p>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Start My Passport <i className="ri-arrow-right-line"></i>
          </a>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-[#F6C344] text-sm"></i>
                ))}
              </div>
              <span className="font-semibold">4.9 / 5</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-award-line text-[#F6C344] text-base"></i>
              <span>A+ BBB Rating</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-calendar-check-line text-[#F6C344] text-base"></i>
              <span>20+ Years</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-shield-check-line text-[#F6C344] text-base"></i>
              <span>Registered State Dept. Courier</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-price-tag-3-line text-[#F6C344] text-base"></i>
              <span>Best Price Guarantee</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}